import { partition, union } from 'es-toolkit';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Annotation, Point2D } from '@paragon/contract-library';
import { isRemoteAction } from '@paragon/sync-client';

import { UpdateInformation } from '../actions/annotations';
import { documentPageDeleted, documentRemoved, jobReset } from './documents';

interface AnnotationsState {
  readonly annotationData: { readonly [pageId: number]: readonly Annotation[] };
  readonly historicalAnnotationData: { readonly [pageId: number]: ReadonlyArray<readonly Annotation[]> };
  readonly futureAnnotationData: { readonly [pageId: number]: ReadonlyArray<readonly Annotation[]> };
  readonly clipboard: readonly Annotation[];
  readonly selectedGuids: readonly string[];
  readonly pagesWithModifications: readonly number[];
  readonly systemAnnotationsEditable: boolean;
  readonly textAnnotationBeingEditedInlineGuid: string | null;
}

const initialState: AnnotationsState = {
  annotationData: {},
  historicalAnnotationData: {},
  futureAnnotationData: {},
  clipboard: [],
  selectedGuids: [],
  pagesWithModifications: [],
  systemAnnotationsEditable: false,
  textAnnotationBeingEditedInlineGuid: null,
};

const annotationsSlice = createSlice({
  name: 'annotations',
  initialState,
  reducers: {
    annotationsLoaded: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly annotations: readonly Annotation[] }>,
    ) => {
      const { pageId, annotations } = action.payload;
      updateAnnotationsOnPage(state, pageId, false, () => zeroIndexify(annotations));
    },
    annotationsUndo: (state, action: PayloadAction<{ readonly pageId: number }>) => {
      const { pageId } = action.payload;

      if (hasRevision(state.historicalAnnotationData, pageId)) {
        const oldAnnotations = state.annotationData[pageId];
        // TODO Handle undefined?
        const newAnnotations = getRevision(state.historicalAnnotationData, pageId)!;

        state.annotationData[pageId] = castDraft(newAnnotations);
        state.historicalAnnotationData = castDraft(popRevision(state.historicalAnnotationData, pageId));
        state.futureAnnotationData = castDraft(addRevision(state.futureAnnotationData, pageId, oldAnnotations));
      }
    },
    annotationsRedo: (state, action: PayloadAction<{ readonly pageId: number }>) => {
      const { pageId } = action.payload;

      if (hasRevision(state.futureAnnotationData, pageId)) {
        const oldAnnotations = state.annotationData[pageId];
        // TODO Handle undefined?
        const newAnnotations = getRevision(state.futureAnnotationData, pageId)!;

        state.annotationData[pageId] = castDraft(newAnnotations);
        state.historicalAnnotationData = castDraft(addRevision(state.historicalAnnotationData, pageId, oldAnnotations));
        state.futureAnnotationData = castDraft(popRevision(state.futureAnnotationData, pageId));
      }
    },
    annotationsAppend: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly annotations: readonly Annotation[] }>,
    ) => {
      const { pageId, annotations } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => {
        const annotationIndices = annotationsOnPage.map((annotation) => annotation.zIndex);
        const highestZIndex = Math.max(-1, ...annotationIndices);

        const newAnnotationsBroughtToFront = annotations.map((annotation, index) => ({
          ...annotation,
          zIndex: highestZIndex + 1 + index,
        }));

        return annotationsOnPage.concat(newAnnotationsBroughtToFront);
      });
    },
    annotationsSaved: (state, action: PayloadAction<{ readonly pageId: number }>) => {
      const savedPageId = action.payload.pageId;

      state.pagesWithModifications = state.pagesWithModifications.filter((pageId) => pageId !== savedPageId);
    },
    annotationToggleSelection: (
      state,
      action: PayloadAction<{ readonly guid: string; readonly exclusive: boolean }>,
    ) => {
      const { exclusive, guid: guidToToggle } = action.payload;

      let newSelectedGuids;

      const isSelected = state.selectedGuids.includes(guidToToggle);
      const multipleSelected = state.selectedGuids.length > 1;

      if (exclusive && (multipleSelected || !isSelected)) {
        newSelectedGuids = [guidToToggle];
      } else if (isSelected) {
        newSelectedGuids = state.selectedGuids.filter((guid) => guid !== guidToToggle);
      } else {
        newSelectedGuids = [...state.selectedGuids, guidToToggle];
      }

      state.selectedGuids = newSelectedGuids;

      if (
        state.textAnnotationBeingEditedInlineGuid !== null &&
        (newSelectedGuids.length !== 1 || !newSelectedGuids.includes(state.textAnnotationBeingEditedInlineGuid))
      ) {
        state.textAnnotationBeingEditedInlineGuid = null;
      }
    },
    annotationSelect: (state, action: PayloadAction<{ readonly guid: string }>) => {
      const { guid } = action.payload;

      state.selectedGuids = state.selectedGuids.includes(guid) ? state.selectedGuids : [...state.selectedGuids, guid];
    },
    annotationsSelect: (state, action: PayloadAction<{ readonly guids: readonly string[] }>) => {
      const { guids } = action.payload;

      state.selectedGuids = union(state.selectedGuids, guids);
    },
    annotationsMove: (
      state,
      action: PayloadAction<{
        readonly delta: Point2D;
        readonly pageId: number;
        readonly guidsToMove: readonly string[];
      }>,
    ) => {
      const { delta, pageId, guidsToMove } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotations) =>
        annotations.map((annotation) => {
          if (!guidsToMove.includes(annotation.guid)) return annotation;
          let movedAnnotation = {
            ...annotation,
            position: {
              x: annotation.position.x + delta.x,
              y: annotation.position.y + delta.y,
            },
            boundingBox: {
              topLeft: {
                x: annotation.boundingBox.topLeft.x + delta.x,
                y: annotation.boundingBox.topLeft.y + delta.y,
              },
              bottomLeft: {
                x: annotation.boundingBox.bottomLeft.x + delta.x,
                y: annotation.boundingBox.bottomLeft.y + delta.y,
              },
              topRight: {
                x: annotation.boundingBox.topRight.x + delta.x,
                y: annotation.boundingBox.topRight.y + delta.y,
              },
              bottomRight: {
                x: annotation.boundingBox.bottomRight.x + delta.x,
                y: annotation.boundingBox.bottomRight.y + delta.y,
              },
            },
          };

          if (annotation.type === 'path' && movedAnnotation.type === 'path') {
            movedAnnotation = {
              ...movedAnnotation,
              segments: annotation.segments.map((point) => ({
                x: point.x + delta.x,
                y: point.y + delta.y,
              })),
            };
          }

          return movedAnnotation;
        }),
      );
    },
    annotationsUpdate: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly updateInformation: readonly UpdateInformation[] }>,
    ) => {
      const { pageId, updateInformation } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => {
        for (const annotationToUpdate of updateInformation) {
          annotationsOnPage = updateAnnotation(
            annotationsOnPage,
            annotationToUpdate.guid,
            annotationToUpdate.updatedProperties,
          );
        }

        return annotationsOnPage;
      });
    },
    annotationsDelete: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly guidsToDelete: readonly string[] }>,
    ) => {
      const { pageId, guidsToDelete } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotations) =>
        annotations.filter((annotation) => !guidsToDelete.includes(annotation.guid)),
      );

      state.selectedGuids = state.selectedGuids.filter((guid) => !guidsToDelete.includes(guid));
    },
    annotationsDeselectAll: (state, action: PayloadAction<{ readonly pageId: number }>) => {
      const { pageId } = action.payload;

      if (state.annotationData[pageId] != null) {
        state.selectedGuids = [];
        state.textAnnotationBeingEditedInlineGuid = null;
      }
    },
    annotationsPaste: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly pastedAnnotations: readonly Annotation[] }>,
    ) => {
      const { pageId, pastedAnnotations } = action.payload;

      // The pasted annotations will be the only things selected
      const pastedGuids = pastedAnnotations.map((annotation) => annotation.guid);

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => annotationsOnPage.concat(pastedAnnotations));

      state.selectedGuids = isRemoteAction(action) ? state.selectedGuids : pastedGuids;
    },
    annotationsCut: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly cutGuids: readonly string[] }>,
    ) => {
      const { pageId, cutGuids } = action.payload;

      const [cutAnnotations, remainingAnnotations] = partition(state.annotationData[pageId], (annotation) =>
        cutGuids.includes(annotation.guid),
      );

      const newSelectedGuids = state.selectedGuids.filter(
        (guid) => !cutAnnotations.some((annotation) => annotation.guid === guid),
      );

      // When synchronized remote clients dispatch a cut action our local clipboard shouldn't change
      state.clipboard = isRemoteAction(action) ? state.clipboard : cutAnnotations;
      state.pagesWithModifications = union(state.pagesWithModifications, [pageId]);
      state.selectedGuids = newSelectedGuids;
      state.annotationData[pageId] = remainingAnnotations;
    },
    annotationsCopy: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly copiedGuids: readonly string[] }>,
    ) => {
      const { pageId, copiedGuids } = action.payload;
      state.clipboard = state.annotationData[pageId].filter((annotation) => copiedGuids.includes(annotation.guid));
    },
    // behavior: bring all selected annotations in front of all
    // other annotations, retaining the order of the selected annotations
    annotationsBringToFront: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly guids: readonly string[] }>,
    ) => {
      const { pageId, guids } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => {
        const [selectedAnnotations, unselectedAnnotations] = partition(annotationsOnPage, (annotation) =>
          guids.includes(annotation.guid),
        );

        const highestZIndex = Math.max(...annotationsOnPage.map((annotation) => annotation.zIndex));

        const selectedAnnotationsWithIncreasedZIndex = selectedAnnotations.map((annotation) => ({
          ...annotation,
          zIndex: annotation.zIndex + highestZIndex + 1,
        }));

        const zeroIndexedAnnotations = zeroIndexify(
          selectedAnnotationsWithIncreasedZIndex.concat(unselectedAnnotations),
        );

        return setSystemAnnotationsToZIndexZero(zeroIndexedAnnotations);
      });
    },
    // behavior: move all selected annotations behind all other annotations,
    // retaining the order of selected annotations
    annotationsSendToBack: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly guids: readonly string[] }>,
    ) => {
      const { pageId, guids } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => {
        const [selectedAnnotations, unselectedAnnotations] = partition(annotationsOnPage, (annotation) =>
          guids.includes(annotation.guid),
        );

        const highestZIndex = Math.max(...annotationsOnPage.map((annotation) => annotation.zIndex));

        const selectedAnnotationsWithDecreasedZIndex = selectedAnnotations.map((annotation) => ({
          ...annotation,
          zIndex: annotation.zIndex - highestZIndex - 1,
        }));

        const zeroIndexedAnnotations = zeroIndexify(
          selectedAnnotationsWithDecreasedZIndex.concat(unselectedAnnotations),
        );

        return setSystemAnnotationsToZIndexZero(zeroIndexedAnnotations);
      });
    },
    // behavior: move all selected annotations in front of the annotation
    // just in front of the foremost selected annotation,
    // retaining the order of selected annotations
    annotationsMoveForward: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly guids: readonly string[] }>,
    ) => {
      const { pageId, guids } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => {
        const [selectedAnnotations, unselectedAnnotations] = partition(annotationsOnPage, (annotation) =>
          guids.includes(annotation.guid),
        );

        selectedAnnotations.sort((a, b) => b.zIndex - a.zIndex);

        const highestSelectedAnnotation = selectedAnnotations[0];
        const newIndexBase = highestSelectedAnnotation.zIndex + 2;

        let counter = 1;
        const selectedAnnotationsWithIncreasedDecimalZIndex = selectedAnnotations.map((annotation) => ({
          ...annotation,
          zIndex: newIndexBase - 0.001 * counter++,
        }));
        const zeroIndexedAnnotations = zeroIndexify(
          selectedAnnotationsWithIncreasedDecimalZIndex.concat(unselectedAnnotations),
        );

        return setSystemAnnotationsToZIndexZero(zeroIndexedAnnotations);
      });
    },
    // behavior: move all selected annotations behind the annotation
    // just behind the rearmost selected annotation, retaining the order of selected annotations
    annotationsMoveBack: (
      state,
      action: PayloadAction<{ readonly pageId: number; readonly guids: readonly string[] }>,
    ) => {
      const { pageId, guids } = action.payload;

      updateAnnotationsOnPage(state, pageId, true, (annotationsOnPage) => {
        const [selectedAnnotations, unselectedAnnotations] = partition(annotationsOnPage, (annotation) =>
          guids.includes(annotation.guid),
        );

        selectedAnnotations.sort((a, b) => a.zIndex - b.zIndex);

        const lowestSelectedAnnotation = selectedAnnotations[0];
        const newIndexBase = lowestSelectedAnnotation.zIndex - 2;

        let counter = 1;
        const selectedAnnotationsWithDecreasedDecimalZIndex = selectedAnnotations.map((annotation) => ({
          ...annotation,
          zIndex: newIndexBase + 0.001 * counter++,
        }));

        const zeroIndexedAnnotations = zeroIndexify(
          selectedAnnotationsWithDecreasedDecimalZIndex.concat(unselectedAnnotations),
        );

        return setSystemAnnotationsToZIndexZero(zeroIndexedAnnotations);
      });
    },
    toggleSystemAnnotationsEditable: (state) => {
      state.systemAnnotationsEditable = !state.systemAnnotationsEditable;
    },
    setTextAnnotationBeingEditedInlineGuid: (state, action: PayloadAction<string | null>) => {
      state.textAnnotationBeingEditedInlineGuid = action.payload;
    },
    confirmJobSeal: () => initialState,
    templateApplied: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(documentPageDeleted, (state, action) => {
      const { pageId } = action.payload;

      state.pagesWithModifications = state.pagesWithModifications.filter(
        (pageWithModificationId) => pageWithModificationId !== pageId,
      );
    });

    builder.addCase(documentRemoved, (state, action) => {
      const { pageIds } = action.payload;

      state.pagesWithModifications = state.pagesWithModifications.filter(
        (pageWithModificationId) => !pageIds.includes(pageWithModificationId),
      );
    });

    builder.addCase(jobReset, () => initialState);
  },
});

export const {
  annotationsLoaded,
  annotationsUndo,
  annotationsRedo,
  annotationsAppend,
  annotationsSaved,
  annotationToggleSelection,
  annotationSelect,
  annotationsSelect,
  annotationsMove,
  annotationsUpdate,
  annotationsDelete,
  annotationsDeselectAll,
  annotationsPaste,
  annotationsCut,
  annotationsCopy,
  annotationsBringToFront,
  annotationsSendToBack,
  annotationsMoveForward,
  annotationsMoveBack,
  toggleSystemAnnotationsEditable,
  setTextAnnotationBeingEditedInlineGuid,
  confirmJobSeal,
  templateApplied,
} = annotationsSlice.actions;
export const annotations = annotationsSlice.reducer;

// this method is used to keep the relative position of which annotations are in front
// in an orderly arrangment. Honestly, you could fling this method willy-nilly, and
// it could only do good.
function zeroIndexify(annotations: readonly Annotation[]) {
  const sortedAnnotations = annotations.toSorted((a, b) => a.zIndex - b.zIndex);
  let newIndex = 0;
  return sortedAnnotations.map((oldAnnotation) => ({
    ...oldAnnotation,
    zIndex: newIndex++,
  }));
}

function updateAnnotationsOnPage(
  state: Draft<AnnotationsState>,
  pageId: number,
  markAsUpdated: boolean,
  annotationModifier: (annotations: readonly Annotation[]) => readonly Annotation[],
) {
  const annotationsOnPage = state.annotationData[pageId] || [];
  const updatedAnnotations = annotationModifier(annotationsOnPage);

  if (markAsUpdated) {
    state.pagesWithModifications = union(state.pagesWithModifications, [pageId]);
    state.historicalAnnotationData = castDraft(
      addRevision(state.historicalAnnotationData, pageId, state.annotationData[pageId]),
    );
  }

  state.annotationData[pageId] = castDraft(updatedAnnotations);
}

function updateAnnotation(
  annotationsForPageId: readonly Annotation[],
  guid: string,
  propertiesToUpdate: Partial<Annotation>,
) {
  const annotationToSelectIndex = annotationsForPageId.findIndex((annotation) => annotation.guid === guid);

  let selectedAnnotation = { ...annotationsForPageId[annotationToSelectIndex], ...propertiesToUpdate } as Annotation;

  // Paths are a special case where updating the position needs to update the segment positions as well
  if (selectedAnnotation.type === 'path' && propertiesToUpdate.position) {
    const xDifference = propertiesToUpdate.position.x - annotationsForPageId[annotationToSelectIndex].position.x;
    const yDifference = propertiesToUpdate.position.y - annotationsForPageId[annotationToSelectIndex].position.y;
    selectedAnnotation = {
      ...selectedAnnotation,
      segments: selectedAnnotation.segments.map((segment) => ({
        x: segment.x + xDifference,
        y: segment.y + yDifference,
      })),
    };
  }

  const updatedAnnotationsForPageId = [...annotationsForPageId];
  updatedAnnotationsForPageId[annotationToSelectIndex] = selectedAnnotation;
  return updatedAnnotationsForPageId;
}

/**
 * @typedef {object} Annotation
 */

/**
 * @typedef {Map<number, Stack<Annotation[]>>} AnnotationRevisionStore
 */

const MAX_REVISIONS_TO_KEEP = 20;

/**
 * @param {AnnotationRevisionStore} pastStates
 * @param {number} pageId
 * @param {Annotation[]} annotations
 */
function addRevision(
  pastStates: { readonly [pageId: number]: ReadonlyArray<readonly Annotation[]> },
  pageId: number,
  annotations: readonly Annotation[],
) {
  let updatedStates = pastStates;

  if (!pastStates[pageId]) {
    updatedStates = { ...pastStates, [pageId]: [] };
  }

  return { ...updatedStates, [pageId]: [annotations, ...updatedStates[pageId]].slice(0, MAX_REVISIONS_TO_KEEP) };
}

/**
 * @param {AnnotationRevisionStore} pastStates
 * @param {number} pageId
 */
function hasRevision(pastStates: { readonly [pageId: number]: ReadonlyArray<readonly Annotation[]> }, pageId: number) {
  return pastStates[pageId] && pastStates[pageId].length > 0;
}

/**
 * @param {AnnotationRevisionStore} pastStates
 * @param {number} pageId
 * @returns {AnnotationRevisionStore}
 */
function getRevision(pastStates: { readonly [pageId: number]: ReadonlyArray<readonly Annotation[]> }, pageId: number) {
  if (!hasRevision(pastStates, pageId)) {
    return undefined;
  }

  return pastStates[pageId][0];
}

/**
 * @param {AnnotationRevisionStore} pastStates
 * @param {number} pageId
 * @returns {AnnotationRevisionStore}
 */
function popRevision(pastStates: { readonly [pageId: number]: ReadonlyArray<readonly Annotation[]> }, pageId: number) {
  if (!hasRevision(pastStates, pageId)) {
    return pastStates;
  }

  return { ...pastStates, [pageId]: pastStates[pageId].slice(1) };
}

/**
 * Sets system annotations to z index 0.
 * Other annotations will have their z index increased to preserve relative order.
 * @param annotations
 */
function setSystemAnnotationsToZIndexZero(annotations: readonly Annotation[]) {
  return annotations.map((annotation) => ({
    ...annotation,
    zIndex: annotation.category === 'system' ? 0 : annotation.zIndex + 1,
  }));
}
